import { useState, useCallback } from 'react';
import {
  VenueBooking,
  CreateBookingRequest,
  CreateBookingResponse,
  BookingFilters,
  BookingStatus,
} from '../../types/venue';
import { apiRequest } from '../../utils/apiUtils';

interface UseBookingsReturn {
  bookings: VenueBooking[];
  loading: boolean;
  error: string | null;
  fetchMyBookings: (status?: BookingStatus) => Promise<void>;
  fetchBookingsByVenue: (venueId: string, filters?: BookingFilters) => Promise<void>;
  createBooking: (data: CreateBookingRequest) => Promise<{
    success: boolean;
    booking?: VenueBooking;
    error?: string;
  }>;
  cancelBooking: (bookingId: string) => Promise<{ success: boolean; error?: string }>;
  confirmBooking: (bookingId: string) => Promise<{ success: boolean; error?: string }>;
}

export const useBookings = (): UseBookingsReturn => {
  const [bookings, setBookings] = useState<VenueBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMyBookings = useCallback(async (status?: BookingStatus) => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = status ? `/bookings/user/my-bookings?status=${status}` : '/bookings/user/my-bookings';
      const response = await apiRequest<VenueBooking[]>(endpoint);

      if (response.data) {
        setBookings(response.data);
      } else {
        setError(response.error || 'Error al cargar reservas');
        setBookings([]);
      }
    } catch (err) {
      setError('Error de conexión al cargar reservas');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBookingsByVenue = useCallback(
    async (venueId: string, filters?: BookingFilters) => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              params.append(key, String(value));
            }
          });
        }

        const queryString = params.toString();
        const endpoint = `/bookings/venue/${venueId}${queryString ? `?${queryString}` : ''}`;
        const response = await apiRequest<VenueBooking[]>(endpoint);

        if (response.data) {
          setBookings(response.data);
        } else {
          setError(response.error || 'Error al cargar reservas del venue');
          setBookings([]);
        }
      } catch (err) {
        setError('Error de conexión al cargar reservas');
        setBookings([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createBooking = useCallback(
    async (data: CreateBookingRequest) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiRequest<CreateBookingResponse>('/bookings', {
          method: 'POST',
          body: data,
        });

        if (response.data) {
          // Refetch bookings to update the list
          await fetchMyBookings();
          return { success: true, booking: response.data.booking };
        } else {
          setError(response.error || 'Error al crear reserva');
          return { success: false, error: response.error };
        }
      } catch (err) {
        const errorMsg = 'Error de conexión al crear reserva';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setLoading(false);
      }
    },
    [fetchMyBookings]
  );

  const cancelBooking = useCallback(
    async (bookingId: string) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiRequest(`/bookings/${bookingId}/cancel`, {
          method: 'PUT',
        });

        if (response.data) {
          // Refetch bookings to update the list
          await fetchMyBookings();
          return { success: true };
        } else {
          setError(response.error || 'Error al cancelar reserva');
          return { success: false, error: response.error };
        }
      } catch (err) {
        const errorMsg = 'Error de conexión al cancelar reserva';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setLoading(false);
      }
    },
    [fetchMyBookings]
  );

  const confirmBooking = useCallback(
    async (bookingId: string) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiRequest(`/bookings/${bookingId}/confirm`, {
          method: 'POST',
        });

        if (response.data) {
          // Update the booking in the local state
          setBookings((prev) =>
            prev.map((booking) =>
              booking.id === bookingId ? { ...booking, status: 'CONFIRMED' as BookingStatus } : booking
            )
          );
          return { success: true };
        } else {
          setError(response.error || 'Error al confirmar reserva');
          return { success: false, error: response.error };
        }
      } catch (err) {
        const errorMsg = 'Error de conexión al confirmar reserva';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    bookings,
    loading,
    error,
    fetchMyBookings,
    fetchBookingsByVenue,
    createBooking,
    cancelBooking,
    confirmBooking,
  };
};
